import React, { useCallback, useEffect, useMemo, useState } from 'react';

import Steps from './components/Steps';
import {
  AccountDebit,
  CreditCard,
  Inspection,
  Installments,
  License,
  PaymentMethods,
  VehicleInfo,
} from './components';
import { useCheckout } from '~/stores';
import { View } from 'react-native';
import { useRoute, type RouteProp } from '@react-navigation/native';
import type { RootStackParamList } from '~/navigation/types';
import useAppNavigation from '~/hooks/useAppNavigation';
import { useUserInfoStore } from '~/stores/userInfo';
import useToast from '~/hooks/useToast';
import useAlertReload from '~/hooks/useAlertReload';
import Loading from '~/components/Loading';
import INSURANCES from '~/utils/insurance';
import { LocalStorage } from '~/storage/LocalStorage';
import type { AnonymousUserState } from '~/storage/types';
import { ANONYMOUS_USER } from '~/storage/constants';

const autoInsuranceSteps = [<License />, <VehicleInfo />, <Inspection />];

const stepByPaymentMethod = {
  ['credit-card']: <CreditCard />,
  ['account-debit']: <AccountDebit />,
  ['bank-slip']: null,
};

const getUserDocument = async (loggedInUserDocument?: string) => {
  if (loggedInUserDocument) return loggedInUserDocument;

  const anonymousUser = await LocalStorage.get<AnonymousUserState>(
    ANONYMOUS_USER
  );

  return anonymousUser?.document ?? undefined;
};

const Checkout = () => {
  const { push, navigate, goBack } = useAppNavigation();
  const { params } =
    useRoute<RouteProp<RootStackParamList, 'MarisaServicos_Checkout'>>();

  const { page, init, form, insurance, finish } = useCheckout();
  const { user } = useUserInfoStore();
  const { showError } = useToast();

  const [isLoading, setIsLoading] = useState(false);
  const [isFinished, setIsFinished] = useState(false);

  useAlertReload();

  useEffect(() => {
    if (params.type) {
      let selectedInsurance = INSURANCES.find(
        ({ type }) => type === params?.type
      )!;

      if (params.paymentMethods) {
        selectedInsurance = {
          ...selectedInsurance,
          paymentMethods: params.paymentMethods,
        };
      }

      if (selectedInsurance) {
        init(selectedInsurance);
      }
    }
  }, [init, params.paymentMethods, params.type]);

  const steps = useMemo(() => {
    const allSteps: (JSX.Element | null)[] = [
      <PaymentMethods />,
      <Installments />,
    ];

    if (form?.paymentMethod && stepByPaymentMethod[form.paymentMethod.method]) {
      allSteps.push(stepByPaymentMethod[form.paymentMethod.method]);
    }

    if (insurance?.type === 'marisa-auto') {
      allSteps.push(...autoInsuranceSteps);
    }

    return allSteps;
  }, [form?.paymentMethod, insurance]);

  const onFinish = useCallback(async () => {
    try {
      setIsLoading(true);

      const userDocument = await getUserDocument(user?.document);

      await finish(userDocument);

      navigate('MarisaServicos_CheckoutSuccess', {
        insuranceType: insurance!.type,
      });
    } catch (err: any) {
      if (err.message) {
        showError({
          title: err.message,
          visibilityTime: 8000,
        });
        goBack();
      } else {
        navigate('MarisaServicos_CheckoutError');
      }
    } finally {
      setIsLoading(false);
    }
  }, [finish, goBack, insurance, navigate, showError, user?.document]);

  useEffect(() => {
    if (params?.step && params?.step > page) {
      if (insurance) {
        push('MarisaServicos_Checkout', { step: 0, type: insurance?.type });
      } else {
        navigate('MarisaServicos_Home');
      }
    }
  }, [insurance, navigate, page, params, push]);

  useEffect(() => {
    if ((params?.step || 0) === steps.length && !isFinished) {
      setIsFinished(true);

      onFinish();
    }
  }, [isFinished, onFinish, params?.step, steps]);

  return (
    <Loading visible={isLoading} label="Estamos processando o pagamento">
      <View className="flex-1 bg-light-100">
        <Steps page={params?.step || 0} pages={steps as JSX.Element[]} />
      </View>
    </Loading>
  );
};

export default Checkout;
